// The chrome around a create/edit/delete form: title, body, error line, and the
// footer with Delete on the left and Cancel/Save on the right.
//
// A page supplies the entity, the fields, and the mutations. It does not supply
// the dirty-discard confirmation, the delete confirmation, the busy states, or
// the error line — those are the same everywhere, which is the whole point.

import type { ReactNode } from "react";
import { Button, Modal } from "@/platform/ui/primitives/index.js";
import type { EntityForm } from "@/platform/client";
import { useEntityFormActions } from "@/platform/ui/patterns/useEntityFormActions.js";

interface EntityFormModalProps<T, F extends object> {
  open: boolean;
  form: EntityForm<T, F>;
  /** e.g. "task" — used to build "New task" / "Edit task" / "Delete task". */
  noun: string;
  /** Overrides the derived title. */
  title?: string;
  /** Names the row in the delete prompt: `Delete "Groceries"?` */
  describe?: (editing: T) => ReactNode;
  /** Defaults to "Create" when creating, "Save" when editing. */
  submitLabel?: string;
  /** Blocks submission (e.g. a required field is blank). */
  canSubmit?: boolean;
  size?: "md" | "lg" | "xl" | "2xl";
  children: ReactNode;
}

export function EntityFormModal<T, F extends object>({
  open,
  form,
  noun,
  title,
  describe,
  submitLabel,
  canSubmit = true,
  size,
  children,
}: EntityFormModalProps<T, F>) {
  // Every close path — backdrop, Escape, Cancel — goes through `close`, so none
  // of them can skip the discard prompt.
  const { close, askDelete } = useEntityFormActions(form, noun, describe);
  const { editing, saving, deleting, error, submit, remove } = form;

  return (
    <Modal
      open={open}
      onClose={close}
      title={title ?? `${editing ? "Edit" : "New"} ${noun}`}
      size={size}
    >
      <div className="space-y-3.5">
        {children}

        {error && <p className="text-xs text-negative">{error}</p>}

        <div className="flex items-center justify-between pt-1">
          <div>
            {remove && (
              <Button variant="danger" onClick={askDelete} disabled={deleting}>
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} disabled={!canSubmit || saving}>
              {submitLabel ?? (editing ? "Save" : "Create")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
